import { Button } from "@/ui-components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/ui-components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui-components/ui/popover";
import { cn } from "@/lib/utils/cn";
import { Check, ChevronsUpDown, Plus, Shield } from "lucide-react";
import { useState } from "react";

export interface SelectableEntity {
  _id: string;
  value: string;
  logoUrl?: string;
  thumbnailUrl?: string;
}

interface EntitySelectorProps {
  label: string;
  entities: SelectableEntity[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onQuickCreate?: () => void;
  isLoading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  createLabel?: string;
}

export const EntitySelector = ({
  label,
  entities,
  selectedId,
  onSelect,
  onQuickCreate,
  isLoading,
  placeholder = "Wählen...",
  searchPlaceholder = "Suchen...",
  emptyText = "Kein Eintrag gefunden.",
  createLabel = "Neu erstellen",
}: EntitySelectorProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedEntity = entities.find((e) => e._id.toString() === selectedId?.toString());
  const selectedEntityLogo = selectedEntity?.thumbnailUrl || selectedEntity?.logoUrl;

  const filteredEntities = entities.filter((entity) =>
    entity.value.toLowerCase().includes(search.toLowerCase())
  );

  const displayedEntities = filteredEntities.slice(0, 3);

  return (
    <div className="flex flex-col gap-3">
      {/* Logo Display */}
      <div className="flex flex-col items-center justify-center">
        <div 
          className={cn(
            "w-24 h-24 rounded-2xl flex items-center justify-center bg-gray-50 mb-2 transition-all duration-200",
            selectedEntityLogo ? "border border-gray-200 shadow-sm" : "border-2 border-dashed border-gray-200"
          )}
        >
          {selectedEntityLogo ? (
            <img 
              src={selectedEntityLogo}
              alt={selectedEntity.value} 
              className="w-16 h-16 object-contain"
            />
          ) : (
            <Shield className="w-8 h-8 text-gray-300" />
          )}
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>

      {/* Selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isLoading}
            className={cn(
              "w-full justify-between h-11 px-3 text-left font-normal",
              !selectedEntity && "text-muted-foreground"
            )}
          >
            <span className="truncate">
              {selectedEntity ? selectedEntity.value : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] sm:w-[260px] p-0" align="center">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder={searchPlaceholder} 
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {filteredEntities.length === 0 && (
                <CommandEmpty className="py-3 px-3 text-center">
                  <p className="text-sm text-muted-foreground mb-3">{emptyText}</p>
                  {onQuickCreate && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full justify-center h-9"
                      onClick={() => {
                        onQuickCreate();
                        setOpen(false);
                      }}
                    >
                      <Plus className="mr-2 h-3.5 w-3.5" /> 
                      {createLabel}
                    </Button>
                  )}
                </CommandEmpty>
              )}
              <CommandGroup>
                {displayedEntities.map((entity) => (
                  <CommandItem
                    key={entity._id}
                    value={entity.value}
                    onSelect={() => {
                      onSelect(entity._id);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedId?.toString() === entity._id.toString() ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {entity.value}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
